import React, { useEffect, useState } from 'react';
import '../../styles/inputs/aiNormalInput.scss';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import CustomSVGs from '../customSVGs/CustomSVGs';
import { eyeCrossedSVG, eyeSVG } from '../../lib/allCustomSVGs';
import { useTranslation } from 'react-i18next';

type props = {
  type?: string;
  required?: boolean;
  value?: string;
  placeholder?: string;
  changeListeners?: ((
    e: React.ChangeEvent<HTMLInputElement>,
    option?: string
  ) => void)[];
  focusListeners?: ((e: React.FocusEvent) => void)[];
  blurListeners?: ((e: React.FocusEvent) => void)[];
  id: string;
  style?: React.CSSProperties;
  classes?: string;
  innerPlaceholder?: string;
  readonly?: boolean;
  pattern?: string;
  title?: string;
  defaultValue?: string;
  autoFocus?: boolean;
  labelClasses?: string;
  disabled?: boolean;
  maxlength?: number;
  minlength?: number;
  infoBanner?: string;
  min?: number;
  max?: number;
  shouldVerify?: boolean;
  inputHoverTitle?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

const AINormalInput = React.forwardRef<HTMLInputElement, props>(
  (
    {
      value,
      changeListeners,
      focusListeners,
      blurListeners,
      placeholder = '',
      type = 'text',
      required,
      id,
      style,
      classes = '',
      innerPlaceholder,
      readonly,
      pattern,
      title,
      defaultValue,
      autoFocus,
      labelClasses,
      disabled,
      maxlength,
      minlength,
      infoBanner,
      min,
      max,
      onKeyDown,
      shouldVerify,
      inputHoverTitle = '',
    }: props,
    ref
  ) => {
    const { t } = useTranslation();
    const [innerValue, setValue] = useState<any>(value);
    const [focused, setFocused] = useState<boolean>(false);
    const [onlyOnFocus, setOnlyOnFocus] = useState<boolean>(false);
    const [passwordShown, setPasswordShown] = useState(false);
    const togglePasswordVisibility = () => {
      setPasswordShown(!passwordShown);
    };

    useEffect(() => {
      setValue(value);
    }, [value]);

    function handleChangeValue(event: React.ChangeEvent<HTMLInputElement>) {
      setValue(event.target.value);
      changeListeners?.forEach((listener) => listener(event));
    }

    function handleFocusElement(event: React.FocusEvent<HTMLInputElement>) {
      focusListeners?.forEach((listener) => listener(event));
      setOnlyOnFocus(true);
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      blurListeners?.forEach((listener) => listener(e));
      setFocused(true);
      setOnlyOnFocus(false);
    };

    return (
      <div title={inputHoverTitle} className='aiNormalInput-outer'>
        <label className={labelClasses} htmlFor={id}>
          {`${placeholder} ${shouldVerify ? '⚠️' : ''}`}
        </label>
        <div className='aiNormalInput'>
          <input
            ref={ref}
            id={id}
            className={classes}
            style={{ fontFamily: 'var(--main-font-secondary)', ...style }}
            type={passwordShown ? 'text' : type}
            value={innerValue}
            onChange={handleChangeValue}
            onFocus={handleFocusElement}
            required={required}
            placeholder={innerPlaceholder}
            readOnly={readonly}
            onBlur={handleBlur}
            data-focused={focused.toString()}
            pattern={pattern}
            title={title}
            defaultValue={defaultValue}
            autoFocus={autoFocus}
            disabled={disabled}
            maxLength={maxlength}
            minLength={minlength}
            onKeyDown={onKeyDown}
            min={min}
            max={max}
          />
          {type === 'password' && (
            <div
              title={passwordShown ? t('HideShowPassword') : t('ShowPassword')}
              onClick={togglePasswordVisibility}
              className={`aiNormalInput__visibility ${
                passwordShown
                  ? 'aiNormalInput__visibility--true'
                  : 'aiNormalInput__visibility--false'
              }`}
            >
              {passwordShown ? (
                <CustomSVGs svg={eyeCrossedSVG} />
              ) : (
                <CustomSVGs svg={eyeSVG} />
              )}
            </div>
          )}
        </div>
        {(onlyOnFocus && isNotEmptyString(title)) ||
        isNotEmptyString(infoBanner) ? (
          <div className='aiNormalInput-validation-text'>
            <span className='slide-in-text'>
              {onlyOnFocus && isNotEmptyString(title)
                ? title
                : isNotEmptyString(infoBanner)
                ? infoBanner
                : ''}
            </span>
          </div>
        ) : null}
      </div>
    );
  }
);

export default AINormalInput;
